import { type ReactElement, forwardRef, useMemo } from 'react';
import clsx from 'clsx';

import {
  Typography,
  Button,
} from '@beeline/design-system-react/extracted-styles';

import { Image } from './Image';
import type { ImageMessageProps } from './types';

import styles from './styles.module.scss';

export const ImageMessage = forwardRef<HTMLDivElement, ImageMessageProps>(
  (
    {
      PrimaryButton,
      SecondaryButton,
      className,
      description,
      imageProps,
      imageSize,
      title,
      disablePadding = false,
    },
    ref
  ): ReactElement => {
    const showButtons = PrimaryButton || SecondaryButton;

    const descriptionWrapper = useMemo(() => {
      const isTypography = typeof description === 'string';
      return isTypography ? (
        <Typography variant='body2' className={styles.description}>
          {description}
        </Typography>
      ) : (
        description
      );
    }, [description]);

    return (
      <div
        className={clsx(
          className,
          styles.imgMessage,
          !disablePadding && styles.padding
        )}
        ref={ref}
      >
        <Image imageSize={imageSize} imageProps={imageProps}/>
        <div className={styles.textBlock}>
          <Typography variant='h6'>{title}</Typography>
          {description && descriptionWrapper}
        </div>

        {showButtons && (
          <div className={styles.buttons}>
            {SecondaryButton && (
              <Button
                variant='outlined'
                size='medium'
                onClick={SecondaryButton.onClick}
                href={SecondaryButton.href}
                target={SecondaryButton.target}
              >
                {SecondaryButton.title}
              </Button>
            )}
            {PrimaryButton && (
              <Button
                variant='contained'
                size='medium'
                onClick={PrimaryButton.onClick}
                href={PrimaryButton.href}
                target={PrimaryButton.target}
              >
                {PrimaryButton.title}
              </Button>
            )}
          </div>
        )}
      </div>
    );
  }
);

ImageMessage.displayName = 'ImageMessage';
